<div class="orgs-container">

  @if (IsInitializing) {
    <div class="loading-overlay"><div class="spinner"></div> Loading...</div>
  }

  @if (!IsInitializing && !IsAuthorizedUser) {
    <div class="empty-state">
      <i class="fa-solid fa-lock"></i>
      <p>Access restricted to system administrators.</p>
    </div>
  }

  <!-- ==================== LEVEL 1: ORGANIZATION LIST ==================== -->
  @if (IsAuthorizedUser && DetailView === 'list') {

    <!-- Page Header -->
    <div class="page-header">
      <div class="page-title-area">
        <div class="page-icon"><i class="fa-solid fa-building"></i></div>
        <div>
          <h1 class="page-title">Organizations</h1>
          <p class="page-subtitle">Activity and usage across all organizations</p>
        </div>
      </div>
      <div class="page-actions">
        <div class="btn-group">
          @for (p of [['this-month','This Month'],['last-month','Last Month'],['7d','7d'],['all-time','All Time'],['custom','Custom']]; track p[0]) {
            <button class="btn-filter" [class.active]="TimePreset === p[0]"
                    (click)="SetTimePreset($any(p[0]))">{{ p[1] }}</button>
          }
        </div>
        @if (TimePreset === 'custom') {
          <div class="custom-range">
            <input type="date" [(ngModel)]="CustomStartDate" />
            <span class="custom-range-sep">to</span>
            <input type="date" [(ngModel)]="CustomEndDate" />
            <button class="btn-apply" (click)="ApplyCustomRange()">Apply</button>
          </div>
        }
        <button class="btn-action" (click)="LoadOrganizations()">
          <i class="fa-solid fa-arrows-rotate"></i> Refresh
        </button>
      </div>
    </div>

    @if (Error) {
      <div class="error-banner">
        <i class="fa-solid fa-circle-exclamation"></i>
        {{ Error }}
        <button (click)="Error = null">&times;</button>
      </div>
    }

    <!-- Search -->
    <div class="search-bar">
      <i class="fa-solid fa-magnifying-glass search-icon"></i>
      <input type="text" class="input-search" [(ngModel)]="OrgSearchTerm"
             placeholder="Search organizations..."
             (keydown)="$event.stopPropagation()"
             (keypress)="$event.stopPropagation()"
             (keyup)="$event.stopPropagation()" />
      @if (OrgSearchTerm) {
        <button class="btn-clear-search" (click)="OrgSearchTerm = ''">&times;</button>
      }
    </div>

    @if (OrgListLoading) {
      <div class="loading-overlay"><div class="spinner"></div> Loading organizations...</div>
    }

    @if (!OrgListLoading) {
      <div class="results-summary">
        <span class="count">{{ FilteredOrganizations.length }} organization{{ FilteredOrganizations.length !== 1 ? 's' : '' }}</span>
      </div>

      @if (FilteredOrganizations.length > 0) {
        <div class="data-table">
          <div class="table-header">
            <span class="col-org clickable" (click)="ToggleSort('organizationName')">
              Organization <i [class]="GetSortIcon('organizationName')"></i>
            </span>
            <span class="col-num clickable" (click)="ToggleSort('conversationCount')">
              Conversations <i [class]="GetSortIcon('conversationCount')"></i>
            </span>
            <span class="col-num clickable" (click)="ToggleSort('activeUsers')">
              Active Users <i [class]="GetSortIcon('activeUsers')"></i>
            </span>
            <span class="col-num clickable" (click)="ToggleSort('totalRuns')">
              Runs <i [class]="GetSortIcon('totalRuns')"></i>
            </span>
            <span class="col-num clickable" (click)="ToggleSort('successRate')">
              Success <i [class]="GetSortIcon('successRate')"></i>
            </span>
            <span class="col-num clickable" (click)="ToggleSort('totalCost')">
              Cost <i [class]="GetSortIcon('totalCost')"></i>
            </span>
            <span class="col-date clickable" (click)="ToggleSort('lastActivityAt')">
              Last Activity <i [class]="GetSortIcon('lastActivityAt')"></i>
            </span>
          </div>
          @for (org of FilteredOrganizations; track org.organizationID) {
            <div class="table-row table-row-clickable" (click)="SelectOrg(org)">
              <span class="col-org">
                <span class="org-name">{{ org.organizationName }}</span>
              </span>
              <span class="col-num">{{ org.conversationCount }}</span>
              <span class="col-num">{{ org.activeUsers }}</span>
              <span class="col-num">{{ org.totalRuns }}</span>
              <span class="col-num">
                <span [class]="GetSuccessRateClass(org.successRate)">{{ FormatPercent(org.successRate) }}</span>
              </span>
              <span class="col-num">{{ FormatCost(org.totalCost) }}</span>
              <span class="col-date">{{ FormatDate(org.lastActivityAt) }}</span>
            </div>
          }
        </div>
      } @else {
        <div class="empty-state">
          <i class="fa-solid fa-building"></i>
          <p>No organizations found for the selected time range.</p>
        </div>
      }
    }
  }

  <!-- ==================== LEVEL 2: ORGANIZATION DETAIL ==================== -->
  @if (IsAuthorizedUser && DetailView === 'org-detail' && SelectedOrg) {
    <div class="detail-container">

      <!-- Header -->
      <div class="detail-header">
        <button class="btn-back" (click)="BackToOrganizations()">
          <i class="fa-solid fa-arrow-left"></i> Back to Organizations
        </button>
        <div class="detail-title">
          <h2>{{ SelectedOrg.organizationName }}</h2>
        </div>
      </div>

      <!-- Summary Stats -->
      <div class="stats-cards">
        <div class="stat-card">
          <span class="stat-value">{{ SelectedOrg.conversationCount }}</span>
          <span class="stat-label">Conversations</span>
        </div>
        <div class="stat-card">
          <span class="stat-value">{{ SelectedOrg.activeUsers }}</span>
          <span class="stat-label">Active Users</span>
        </div>
        <div class="stat-card">
          <span class="stat-value">{{ SelectedOrg.totalRuns }}</span>
          <span class="stat-label">Total Runs</span>
        </div>
        <div class="stat-card">
          <span class="stat-value" [class]="GetSuccessRateClass(SelectedOrg.successRate)">{{ FormatPercent(SelectedOrg.successRate) }}</span>
          <span class="stat-label">Success Rate</span>
        </div>
        <div class="stat-card">
          <span class="stat-value cost-value">{{ FormatCost(SelectedOrg.totalCost) }}</span>
          <span class="stat-label">Total Cost</span>
        </div>
      </div>

      <!-- Time Range + Sub-tabs -->
      <div class="tab-bar">
        <div class="tab-group">
          <button class="tab-btn" [class.active]="OrgSubTab === 'activity'" (click)="SwitchSubTab('activity')">
            <i class="fa-solid fa-chart-line"></i> Activity
          </button>
          <button class="tab-btn" [class.active]="OrgSubTab === 'components'" (click)="SwitchSubTab('components')">
            <i class="fa-solid fa-puzzle-piece"></i> Components
          </button>
          <button class="tab-btn" [class.active]="OrgSubTab === 'costs'" (click)="SwitchSubTab('costs')">
            <i class="fa-solid fa-dollar-sign"></i> Costs
          </button>
        </div>
        <div class="btn-group">
          @for (p of [['this-month','This Month'],['last-month','Last Month'],['7d','7d'],['all-time','All Time'],['custom','Custom']]; track p[0]) {
            <button class="btn-filter btn-filter-sm" [class.active]="TimePreset === p[0]"
                    (click)="SetTimePreset($any(p[0]))">{{ p[1] }}</button>
          }
        </div>
        @if (TimePreset === 'custom') {
          <div class="custom-range">
            <input type="date" [(ngModel)]="CustomStartDate" />
            <span class="custom-range-sep">to</span>
            <input type="date" [(ngModel)]="CustomEndDate" />
            <button class="btn-apply" (click)="ApplyCustomRange()">Apply</button>
          </div>
        }
      </div>

      @if (Error) {
        <div class="error-banner">
          <i class="fa-solid fa-circle-exclamation"></i>
          {{ Error }}
          <button (click)="Error = null">&times;</button>
        </div>
      }

      <!-- ── Activity Sub-Tab ── -->
      @if (OrgSubTab === 'activity') {

        <!-- Daily Activity Chart -->
        <div class="section-card">
          <div class="section-header">
            <h3 class="section-title"><i class="fa-solid fa-chart-column"></i> Daily Activity</h3>
          </div>
          @if (ActivityLoading) {
            <div class="loading-overlay compact"><div class="spinner"></div></div>
          } @else if (DailyActivity.length > 0) {
            <div class="chart-container">
              <div class="chart-with-axis">
                <div class="y-axis">
                  @for (tick of ActivityYAxisTicks; track $index) {
                    <span class="y-tick">{{ FormatAxisCount(tick) }}</span>
                  }
                </div>
                <div class="chart-area">
                  @for (tick of ActivityYAxisTicks; track $index) {
                    @if ($index > 0) {
                      <div class="gridline" [style.bottom.%]="GetGridlinePosition(tick, NiceMaxActivity)"></div>
                    }
                  }
                  <div class="bar-chart">
                    @for (row of DailyActivity; track row.day) {
                      <div class="bar-col" [class.bar-col-zero]="row.conversationCount === 0"
                           [title]="FormatShortDate(row.day) + ': ' + row.conversationCount + ' conversations'">
                        @if (row.conversationCount > 0) {
                          <span class="bar-value">{{ row.conversationCount }}</span>
                        }
                        <div class="bar bar-activity" [style.height.%]="GetActivityBarHeight(row.conversationCount)"></div>
                        <span class="bar-label">{{ GetShortDay(row.day) }}</span>
                      </div>
                    }
                  </div>
                </div>
              </div>
            </div>
          } @else {
            <div class="empty-state-sm">
              <i class="fa-solid fa-chart-column"></i>
              <p>No daily activity data available</p>
            </div>
          }
        </div>

        <!-- Per-Person Breakdown -->
        <div class="section-card">
          <div class="section-header">
            <h3 class="section-title"><i class="fa-solid fa-users"></i> Activity by Contact</h3>
            @if (SelectedPersonID) {
              <button class="btn-sm" (click)="ClearPersonFilter()">
                <i class="fa-solid fa-filter-circle-xmark"></i> Clear Filter
              </button>
            }
          </div>
          @if (ActivityLoading) {
            <div class="loading-overlay compact"><div class="spinner"></div></div>
          } @else if (PersonActivities.length > 0) {
            <div class="data-table">
              <div class="table-header">
                <span class="col-name">Contact</span>
                <span class="col-num">Conversations</span>
              </div>
              @for (person of PersonActivities; track person.personID) {
                <div class="table-row table-row-clickable" [class.table-row-selected]="SelectedPersonID === person.personID"
                     (click)="FilterByPerson(person.personID)">
                  <span class="col-name">
                    {{ person.personName }}
                    @if (SelectedPersonID === person.personID) {
                      <i class="fa-solid fa-filter filter-active-icon"></i>
                    }
                  </span>
                  <span class="col-num">{{ person.conversationCount }}</span>
                </div>
              }
            </div>
          } @else {
            <div class="empty-state-sm">
              <i class="fa-solid fa-users"></i>
              <p>No contact activity data available</p>
            </div>
          }
        </div>

        <!-- Conversations List -->
        <div class="section-card">
          <div class="section-header">
            <h3 class="section-title">
              <i class="fa-solid fa-comments"></i> Conversations
              @if (SelectedPersonID) {
                <span class="filter-badge">Filtered</span>
              }
            </h3>
            <span class="section-badge">{{ Conversations.length }} total</span>
          </div>
          @if (ConversationsLoading) {
            <div class="loading-overlay compact"><div class="spinner"></div></div>
          } @else if (Conversations.length > 0) {
            <div class="data-table">
              <div class="table-header">
                <span class="col-name">Name</span>
                <span class="col-contact">Contact</span>
                <span class="col-num">Exchanges</span>
                <span class="col-status">Failures</span>
                <span class="col-date">Created</span>
              </div>
              @for (conv of Conversations; track conv.conversationID) {
                <div class="table-row table-row-clickable" (click)="SelectConversation(conv)">
                  <span class="col-name">{{ conv.name || 'Conversation' }}</span>
                  <span class="col-contact">{{ conv.personName }}</span>
                  <span class="col-num">{{ conv.exchangeCount }}</span>
                  <span class="col-status">
                    @if (conv.hasFailures) {
                      <span class="failure-dot" title="Has failures"></span>
                    }
                  </span>
                  <span class="col-date">{{ FormatDate(conv.createdAt) }}</span>
                </div>
              }
            </div>
          } @else {
            <div class="empty-state-sm">
              <i class="fa-solid fa-comments"></i>
              <p>No conversations found</p>
            </div>
          }
        </div>
      }

      <!-- ── Components Sub-Tab ── -->
      @if (OrgSubTab === 'components') {
        <div class="section-card">
          <div class="section-header">
            <h3 class="section-title"><i class="fa-solid fa-puzzle-piece"></i> Components</h3>
            <span class="section-badge">{{ FilteredComponents.length }} of {{ Components.length }}</span>
          </div>

          <!-- Search & Filter Bar -->
          <div class="comp-filter-bar">
            <div class="comp-search-wrap">
              <i class="fa-solid fa-magnifying-glass comp-search-icon"></i>
              <input type="text" class="comp-search-input" [(ngModel)]="ComponentSearch"
                     placeholder="Search components..."
                     (input)="ApplyComponentFilters()" />
            </div>
            @if (ComponentTypes.length > 1) {
              <select class="comp-type-select" [(ngModel)]="ComponentTypeFilter" (change)="ApplyComponentFilters()">
                <option value="">All Types</option>
                @for (t of ComponentTypes; track t) {
                  <option [value]="t">{{ t }}</option>
                }
              </select>
            }
          </div>

          @if (ComponentsLoading) {
            <div class="loading-overlay compact"><div class="spinner"></div></div>
          } @else if (FilteredComponents.length > 0) {
            <div class="data-table">
              <div class="table-header">
                <span class="col-name sortable" (click)="SetComponentSort('componentName')">
                  Component <i class="fa-solid" [ngClass]="GetComponentSortIcon('componentName')"></i>
                </span>
                <span class="col-type">Type</span>
                <span class="col-num sortable" (click)="SetComponentSort('versionCount')">
                  Versions <i class="fa-solid" [ngClass]="GetComponentSortIcon('versionCount')"></i>
                </span>
                <span class="col-rating sortable" (click)="SetComponentSort('avgRating')">
                  Rating <i class="fa-solid" [ngClass]="GetComponentSortIcon('avgRating')"></i>
                </span>
                <span class="col-num sortable" (click)="SetComponentSort('totalViews')">
                  Views <i class="fa-solid" [ngClass]="GetComponentSortIcon('totalViews')"></i>
                </span>
                <span class="col-date sortable" (click)="SetComponentSort('lastGenerated')">
                  Last Generated <i class="fa-solid" [ngClass]="GetComponentSortIcon('lastGenerated')"></i>
                </span>
              </div>
              @for (comp of FilteredComponents; track comp.componentID) {
                <div class="table-row">
                  <span class="col-name">
                    <span class="comp-name">{{ comp.componentName }}</span>
                    @if (comp.title) {
                      <span class="comp-title">{{ comp.title }}</span>
                    }
                  </span>
                  <span class="col-type">
                    @if (comp.type) {
                      <span class="type-badge">{{ comp.type }}</span>
                    } @else {
                      <span class="text-muted">&mdash;</span>
                    }
                  </span>
                  <span class="col-num">{{ comp.versionCount }}</span>
                  <span class="col-rating">
                    @if (comp.avgRating !== null) {
                      <span class="star-rating">
                        @for (filled of GetStarArray(comp.avgRating); track $index) {
                          @if (filled) {
                            <i class="fa-solid fa-star star-filled"></i>
                          } @else {
                            <i class="fa-regular fa-star star-empty"></i>
                          }
                        }
                      </span>
                      <span class="rating-number">{{ comp.avgRating.toFixed(1) }}</span>
                    } @else {
                      <span class="text-muted">&mdash;</span>
                    }
                  </span>
                  <span class="col-num">{{ comp.totalViews }}</span>
                  <span class="col-date">{{ FormatDate(comp.lastGenerated) }}</span>
                </div>
              }
            </div>
          } @else if (Components.length > 0) {
            <div class="empty-state-sm">
              <i class="fa-solid fa-magnifying-glass"></i>
              <p>No components match your search</p>
            </div>
          } @else {
            <div class="empty-state-sm">
              <i class="fa-solid fa-puzzle-piece"></i>
              <p>No components found for this time range</p>
            </div>
          }
        </div>
      }

      <!-- ── Costs Sub-Tab ── -->
      @if (OrgSubTab === 'costs') {

        @if (CostLoading) {
          <div class="loading-overlay"><div class="spinner"></div> Loading cost data...</div>
        } @else {

          <!-- KPI Cards -->
          <div class="stats-cards stats-cards-3">
            <div class="stat-card">
              <span class="stat-value cost-value">{{ FormatCost(MonthlyTotalCost) }}</span>
              <span class="stat-label">Total Cost</span>
            </div>
            <div class="stat-card">
              <span class="stat-value">{{ FormatTokens(MonthlyTotalTokens) }}</span>
              <span class="stat-label">Total Tokens</span>
            </div>
            <div class="stat-card">
              <span class="stat-value">{{ FormatCostPrecise(AvgCostPerRun) }}</span>
              <span class="stat-label">Avg Cost / Run</span>
            </div>
          </div>

          <!-- Cost by Model -->
          <div class="section-card">
            <div class="section-header">
              <h3 class="section-title"><i class="fa-solid fa-microchip"></i> Cost by Model</h3>
            </div>
            @if (ModelCosts.length > 0) {
              <div class="model-breakdown">
                @for (row of ModelCosts; track row.modelName + row.vendorName) {
                  <div class="model-row">
                    <div class="model-info">
                      <span class="model-name">{{ row.modelName }}</span>
                      <span class="model-vendor">{{ row.vendorName }}</span>
                    </div>
                    <div class="model-bar-wrap">
                      <div class="model-bar" [style.width.%]="GetModelBarPercent(row.totalCost)"></div>
                    </div>
                    <div class="model-stats">
                      <span class="model-cost">{{ FormatCostPrecise(row.totalCost) }}</span>
                      <span class="model-tokens">{{ FormatTokens(row.totalTokens) }} tokens</span>
                    </div>
                  </div>
                }
              </div>
            } @else {
              <div class="empty-state-sm">
                <i class="fa-solid fa-microchip"></i>
                <p>No model cost data available</p>
              </div>
            }
          </div>

          <!-- Cost by Person -->
          <div class="section-card">
            <div class="section-header">
              <h3 class="section-title"><i class="fa-solid fa-users"></i> Cost by Person</h3>
            </div>
            @if (ContactCosts.length > 0) {
              <div class="model-breakdown">
                @for (row of ContactCosts; track row.personID) {
                  <div class="model-row">
                    <div class="model-info">
                      <span class="model-name">{{ row.personName }}</span>
                      <span class="model-vendor">{{ row.runCount }} runs</span>
                    </div>
                    <div class="model-bar-wrap">
                      <div class="model-bar bar-person" [style.width.%]="GetContactCostBarPercent(row.totalCost)"></div>
                    </div>
                    <div class="model-stats">
                      <span class="model-cost">{{ FormatCostPrecise(row.totalCost) }}</span>
                      <span class="model-tokens">{{ FormatTokens(row.totalTokens) }} tokens</span>
                    </div>
                  </div>
                }
              </div>
            } @else {
              <div class="empty-state-sm">
                <i class="fa-solid fa-users"></i>
                <p>No person cost data available</p>
              </div>
            }
          </div>
        }
      }
    </div>
  }

  <!-- ==================== LEVEL 3: CONVERSATION DETAIL ==================== -->
  @if (IsAuthorizedUser && DetailView === 'conversation' && SelectedConversation && SelectedOrg) {
    <div class="detail-container">

      <!-- Header -->
      <div class="detail-header">
        <button class="btn-back" (click)="BackToOrgDetail()">
          <i class="fa-solid fa-arrow-left"></i> Back to {{ SelectedOrg.organizationName }}
        </button>
        <div class="detail-title">
          <h2>{{ SelectedConversation.name || 'Conversation' }}</h2>
        </div>
      </div>

      <!-- Conversation Metadata -->
      <div class="detail-summary">
        <div class="summary-item">
          <span class="summary-label">Contact</span>
          <span class="summary-value">{{ SelectedConversation.personName }}</span>
        </div>
        <div class="summary-item">
          <span class="summary-label">Created</span>
          <span class="summary-value">{{ FormatDate(SelectedConversation.createdAt) }}</span>
        </div>
        <div class="summary-item">
          <span class="summary-label">Exchanges</span>
          <span class="summary-value">{{ SelectedConversation.exchangeCount }}</span>
        </div>
        @if (SelectedConversation.hasFailures) {
          <div class="summary-item">
            <span class="summary-label">Status</span>
            <span class="summary-value status-failed-text">Has Failures</span>
          </div>
        }
      </div>

      <!-- Exchanges -->
      @if (ExchangesLoading) {
        <div class="loading-overlay"><div class="spinner"></div> Loading conversation...</div>
      } @else {
        <div class="exchange-list">
          @for (ex of Exchanges; track ex.id) {
            <!-- User Message -->
            @if (ex.role === 'User') {
              <div class="chat-row user-row">
                <div class="chat-bubble user-bubble">
                  <div class="bubble-header">
                    <i class="fa-solid fa-user"></i> <span>User</span>
                    <span class="bubble-time">{{ FormatDate(ex.createdAt) }}</span>
                  </div>
                  @if (ex.message && IsFormMessage(ex.message)) {
                    <div class="bubble-body form-body">
                      <div class="form-header"><i class="fa-solid fa-list-check"></i> Form Response</div>
                      @for (field of ParseFormFields(ex.message); track field.label) {
                        <div class="form-field">
                          <span class="form-label">{{ field.label }}</span>
                          <span class="form-value">{{ field.displayValue }}</span>
                        </div>
                      }
                    </div>
                  } @else {
                    <div class="bubble-body">{{ ex.message || '\u2014' }}</div>
                  }
                </div>
              </div>
            }

            <!-- AI Message -->
            @if (ex.role === 'AI') {
              <div class="chat-row ai-row">
                <div class="chat-bubble ai-bubble">
                  <div class="bubble-header">
                    <i class="fa-solid fa-robot"></i> <span>Skip</span>
                    <span class="bubble-time">{{ FormatDate(ex.createdAt) }}</span>
                    @if (ex.agentRunStatus) {
                      <span class="status-badge sm" [ngClass]="GetStatusClass(ex.agentRunStatus)">{{ ex.agentRunStatus }}</span>
                    }
                    @if (ex.agentRunDurationMs) {
                      <span class="step-duration" [ngClass]="GetDurationClass(ex.agentRunDurationMs)">{{ FormatDuration(ex.agentRunDurationMs) }}</span>
                    }
                  </div>
                  @if (ex.message && IsFormMessage(ex.message)) {
                    <div class="bubble-body form-body">
                      <div class="form-header"><i class="fa-solid fa-list-check"></i> Form Response</div>
                      @for (field of ParseFormFields(ex.message); track field.label) {
                        <div class="form-field">
                          <span class="form-label">{{ field.label }}</span>
                          <span class="form-value">{{ field.displayValue }}</span>
                        </div>
                      }
                    </div>
                  } @else {
                    <div class="bubble-body">{{ ex.message || '\u2014' }}</div>
                  }

                  <!-- Error display -->
                  @if (ex.agentRunError) {
                    <div class="exchange-error">
                      <i class="fa-solid fa-circle-exclamation"></i>
                      <pre>{{ ex.agentRunError }}</pre>
                    </div>
                  }

                  <!-- Agent Run button -->
                  @if (ex.aiAgentRunID) {
                    <div class="bubble-actions">
                      <button class="btn-sm" (click)="OpenAgentRunForm(ex.aiAgentRunID)">
                        <i class="fa-solid fa-external-link-alt"></i> View Agent Run
                      </button>
                    </div>
                  }
                </div>
              </div>
            }
          }

          @if (Exchanges.length === 0) {
            <div class="empty-state-sm">
              <i class="fa-solid fa-comments"></i>
              <p>No exchanges found in this conversation</p>
            </div>
          }
        </div>
      }
    </div>
  }
</div>
